import React, { type ReactNode } from 'react';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';

export interface EventTimelineTag {
  /** Stable key for the tag. */
  key: string;
  /** Visible tag label. */
  label: string;
  /** Optional value displayed after the label. */
  value?: string;
}

export interface EventTimelineItem {
  /** Stable key for the timeline item. */
  key: string;
  /** Primary event label. */
  label: string;
  /** Compact time or value displayed at the end of the event header. */
  value: string;
  /** Optional tooltip that expands the compact value. */
  valueTooltip?: string;
  /** Semantic event tone. */
  tone?: 'default' | 'success' | 'warning' | 'danger';
  /** Compact metadata tags displayed below the event header. */
  tags?: EventTimelineTag[];
  /** Legacy detail values rendered as tags. */
  details?: { key: string; label: string; value: string }[];
  /** Optional leading icon displayed inside the timeline rail. */
  icon?: ReactNode;
  /** Optional translated event description. */
  description?: ReactNode;
  /** Optional status or action displayed beside the event value. */
  status?: ReactNode;
  /** Optional rich content displayed below the event metadata. */
  content?: ReactNode;
}

interface EventTimelineProps {
  /** Chronologically ordered events. */
  items: EventTimelineItem[];
  /** Empty-state label. */
  emptyLabel: string;
  /** Optional empty-state color classes. */
  emptyToneClassName?: string;
  /** Optional item-surface class overrides by tone. */
  itemToneClassNameByTone?: Partial<
    Record<NonNullable<EventTimelineItem['tone']>, string>
  >;
  /** Optional timeline-rail class overrides by tone. */
  railToneClassNameByTone?: Partial<
    Record<NonNullable<EventTimelineItem['tone']>, string>
  >;
}

const DEFAULT_ITEM_TONE_CLASS_NAME_BY_TONE: Record<
  NonNullable<EventTimelineItem['tone']>,
  string
> = {
  default: 'border-slate-100 bg-slate-50/80',
  success: 'border-success-100 bg-success-50/60',
  warning: 'border-warning-100 bg-warning-50/60',
  danger: 'border-danger-100 bg-danger-50/60',
};

const DEFAULT_RAIL_TONE_CLASS_NAME_BY_TONE: Record<
  NonNullable<EventTimelineItem['tone']>,
  string
> = {
  default: 'bg-slate-300',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
};

export const EventTimeline: React.FC<EventTimelineProps> = ({
  items,
  emptyLabel,
  emptyToneClassName = 'text-slate-500',
  itemToneClassNameByTone,
  railToneClassNameByTone,
}) => {
  const mergedItemToneClassNameByTone = {
    ...DEFAULT_ITEM_TONE_CLASS_NAME_BY_TONE,
    ...itemToneClassNameByTone,
  };
  const mergedRailToneClassNameByTone = {
    ...DEFAULT_RAIL_TONE_CLASS_NAME_BY_TONE,
    ...railToneClassNameByTone,
  };

  if (items.length === 0) {
    return (
      <Text size="tiny" className={emptyToneClassName}>
        {emptyLabel}
      </Text>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        const tone = item.tone ?? 'default';
        const tags =
          item.tags ??
          item.details?.map((detail) => ({
            key: detail.key,
            label: detail.label,
            value: detail.value,
          }));

        return (
          <div key={item.key} className="flex gap-3">
            <div className="flex w-8 shrink-0 flex-col items-center pt-1">
              {item.icon ? (
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-white ${mergedRailToneClassNameByTone[tone]}`}
                >
                  {item.icon}
                </span>
              ) : (
                <span
                  className={`mt-0.5 h-2.5 w-2.5 rounded-full ${mergedRailToneClassNameByTone[tone]}`}
                />
              )}
              {index < items.length - 1 ? (
                <span className="mt-1 min-h-6 w-px flex-1 bg-slate-200" />
              ) : null}
            </div>
            <div
              className={`min-w-0 flex-1 rounded-xl border px-3 py-2 ${mergedItemToneClassNameByTone[tone]}`}
            >
              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                <div className="min-w-0">
                  <Text size="small" weight="medium" className="text-slate-700">
                    {item.label}
                  </Text>
                  {item.description ? (
                    <div className="mt-0.5 text-sm text-slate-600">
                      {item.description}
                    </div>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {item.status}
                  {item.valueTooltip ? (
                    <Tooltip
                      content={item.valueTooltip}
                      size="xs"
                      placement="top"
                    >
                      <span className="inline-flex">
                        <Text size="tiny" className="text-right text-slate-500">
                          {item.value}
                        </Text>
                      </span>
                    </Tooltip>
                  ) : (
                    <Text size="tiny" className="text-right text-slate-500">
                      {item.value}
                    </Text>
                  )}
                </div>
              </div>
              {tags && tags.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span
                      key={`${item.key}-${tag.key}`}
                      className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600"
                    >
                      {tag.value ? `${tag.label}: ${tag.value}` : tag.label}
                    </span>
                  ))}
                </div>
              ) : null}
              {item.content ? (
                <div className="mt-3 border-t border-slate-200/80 pt-3">
                  {item.content}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};
